import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, Heartbeat, Drop, Warning } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

const DGS_TARGETS = {
  jejum: { green: 95, amber: 105, label: 'Jejum' },
  pos_prandial_1h: { green: 140, amber: 160, label: '1h pós-prandial' },
  pos_prandial_2h: { green: 120, amber: 140, label: '2h pós-prandial' },
  casual: { label: 'Casual' },
};
function dgsZone(glu, moment) {
  if (glu == null || !moment || !DGS_TARGETS[moment] || !DGS_TARGETS[moment].green) return null;
  const t = DGS_TARGETS[moment];
  if (glu < t.green) return 'green';
  if (glu < t.amber) return 'amber';
  return 'red';
}
const ZONE_COLOR = { green: 'bg-green-100 text-green-700 border-green-300', amber: 'bg-amber-100 text-amber-700 border-amber-300', red: 'bg-red-100 text-red-700 border-red-300' };

export default function ClinicoClientePage() {
  const confirmDelete = useConfirmDelete();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], systolic: '', diastolic: '', glucose_mg_dl: '', meal_moment: '', notes: '' });

  const fetchRec = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/my/clinical-records`); setRecords(Array.isArray(data) ? data : []); } catch { toast.error('Erro a carregar'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchRec(); }, [fetchRec]);

  const save = async () => {
    const payload = {
      date: form.date,
      systolic: form.systolic ? parseInt(form.systolic) : null,
      diastolic: form.diastolic ? parseInt(form.diastolic) : null,
      glucose_mg_dl: form.glucose_mg_dl ? parseFloat(form.glucose_mg_dl) : null,
      meal_moment: form.meal_moment || null,
      notes: form.notes || '',
    };
    if (payload.systolic === null && payload.diastolic === null && payload.glucose_mg_dl === null) {
      toast.error('Preenche pelo menos um valor');
      return;
    }
    try {
      await axios.post(`${API}/api/my/clinical-records`, payload);
      toast.success('Registo guardado');
      setShowAdd(false);
      setForm({ date: new Date().toISOString().split('T')[0], systolic: '', diastolic: '', glucose_mg_dl: '', meal_moment: '', notes: '' });
      fetchRec();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const remove = async (id, isMine) => {
    if (!isMine) { toast.error('Só podes apagar registos que tu inseriste'); return; }
    const rec = records.find(r => r.id === id);
    const ok = await confirmDelete({ description: `Vai apagar o registo de ${rec?.date || ''}.` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/my/clinical-records/${id}`); toast.success('Eliminado'); fetchRec(); } catch { toast.error('Erro'); }
  };

  const chartData = [...records].slice(0, 30).reverse().map(r => ({
    date: (r.date || '').slice(5),
    sys: r.systolic ?? null,
    dia: r.diastolic ?? null,
    glu: r.glucose_mg_dl ?? null,
  }));
  const hasBP = chartData.some(p => p.sys != null || p.dia != null);
  const hasGlu = chartData.some(p => p.glu != null);
  const lastSevere = records.find(r => r.severe);

  return (
    <div className="fade-in max-w-xl mx-auto" data-testid="clinico-client-page">
      <div className="flex items-start justify-between mb-1">
        <h1 className="font-heading text-2xl text-brand-text">Tensão arterial</h1>
        <button onClick={() => setShowAdd(true)} className="bg-brand-green text-white rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1" data-testid="clinico-client-add">
          <Plus size={12} /> Novo
        </button>
      </div>
      <p className="text-xs text-brand-muted mb-5">Regista os teus valores em casa. A tua nutricionista vê estes dados.</p>

      {lastSevere && (
        <div className="bg-red-50 border border-red-300 rounded-2xl p-3 mb-3 flex items-start gap-2" data-testid="clinico-client-alert">
          <Warning size={18} weight="fill" className="text-red-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-xs font-semibold text-red-700">Valores fora do limite clínico</p>
            <p className="text-[10px] text-red-600">{(lastSevere.alerts || []).join('; ')} em {fmtDatePt(lastSevere.date)}. A tua nutricionista foi avisada.</p>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-xs text-brand-muted text-center py-10">A carregar...</p>
      ) : records.length === 0 ? (
        <div className="bg-white border border-brand-border rounded-2xl p-8 text-center" data-testid="clinico-client-empty">
          <Heartbeat size={32} className="mx-auto text-brand-placeholder mb-2" weight="duotone" />
          <p className="text-xs text-brand-muted">Ainda sem registos. Toca em "Novo" para adicionar o primeiro.</p>
        </div>
      ) : (
        <>
          {(hasBP || hasGlu) && (
            <div className="bg-white border border-brand-border rounded-2xl p-3 mb-3" data-testid="clinico-client-chart">
              <p className="text-[11px] text-brand-muted mb-1">Evolução</p>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 0, left: -20 }}>
                  <CartesianGrid stroke="#E8E5DE" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                  <YAxis yAxisId="l" domain={[40, 200]} tick={{ fontSize: 9 }} />
                  <YAxis yAxisId="r" orientation="right" domain={[40, 250]} tick={{ fontSize: 9 }} />
                  <RTooltip contentStyle={{ fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  {hasBP && <Line yAxisId="l" type="monotone" dataKey="sys" stroke="#657161" strokeWidth={2} name="Sistólica" connectNulls dot={{ r: 2 }} />}
                  {hasBP && <Line yAxisId="l" type="monotone" dataKey="dia" stroke="#BBD6D5" strokeWidth={2} name="Diastólica" connectNulls dot={{ r: 2 }} />}
                  {hasGlu && <Line yAxisId="r" type="monotone" dataKey="glu" stroke="#D7BD7B" strokeWidth={2} name="Glicemia" connectNulls dot={{ r: 2 }} />}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <ul className="space-y-1.5">
            {records.map(r => (
              <li key={r.id} className={`bg-white border rounded-2xl p-3 ${r.severe ? 'border-red-300' : 'border-brand-border'}`} data-testid={`clinico-client-row-${r.id}`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-brand-text flex items-center gap-1.5">
                    {r.severe && <Warning size={12} weight="fill" className="text-red-500" />}
                    {r.date}
                  </p>
                  <div className="flex items-center gap-2">
                    {r.self_reported && <span className="text-[9px] bg-brand-gold/15 text-brand-gold-hover px-1.5 py-0.5 rounded-full">teu</span>}
                    {r.self_reported && <button onClick={() => remove(r.id, true)} className="text-brand-placeholder hover:text-red-500"><Trash size={12} /></button>}
                  </div>
                </div>
                <div className="flex gap-3 mt-1 text-[11px] text-brand-text flex-wrap">
                  {(r.systolic != null || r.diastolic != null) && (
                    <span className="flex items-center gap-1"><Heartbeat size={11} className="text-brand-green" /> <strong>{r.systolic ?? '-'}/{r.diastolic ?? '-'}</strong> mmHg</span>
                  )}
                  {r.glucose_mg_dl != null && (() => {
                    const zone = r.dgs_zone || dgsZone(r.glucose_mg_dl, r.meal_moment);
                    const colorCls = zone ? ZONE_COLOR[zone] : 'bg-gray-100 text-gray-600 border-gray-300';
                    const momentLabel = r.meal_moment ? (DGS_TARGETS[r.meal_moment]?.label || r.meal_moment) : 'momento não registado';
                    return (
                      <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full border ${colorCls}`} data-testid={`glu-zone-${zone || 'grey'}`}>
                        <Drop size={11} /> <strong>{r.glucose_mg_dl}</strong> mg/dL <span className="text-[9px] opacity-80">· {momentLabel}</span>
                      </span>
                    );
                  })()}
                </div>
                {r.notes && <p className="text-[10px] text-brand-muted italic mt-1">"{r.notes}"</p>}
              </li>
            ))}
          </ul>
        </>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="rounded-3xl border-brand-border" data-testid="clinico-client-dialog">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Novo registo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Data</Label>
              <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-xl border-brand-border" data-testid="clinico-client-date" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Sistólica (mmHg)</Label>
                <Input type="number" min="50" max="260" value={form.systolic} onChange={e => setForm({ ...form, systolic: e.target.value })} className="rounded-xl border-brand-border" placeholder="120" data-testid="clinico-client-sys" />
              </div>
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Diastólica (mmHg)</Label>
                <Input type="number" min="30" max="160" value={form.diastolic} onChange={e => setForm({ ...form, diastolic: e.target.value })} className="rounded-xl border-brand-border" placeholder="80" data-testid="clinico-client-dia" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Glicemia (mg/dL)</Label>
              <Input type="number" step="0.1" min="30" max="600" value={form.glucose_mg_dl} onChange={e => setForm({ ...form, glucose_mg_dl: e.target.value })} className="rounded-xl border-brand-border" placeholder="95" data-testid="clinico-client-glu" />
            </div>
            {form.glucose_mg_dl && (
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Momento da medição</Label>
                <select value={form.meal_moment} onChange={e => setForm({ ...form, meal_moment: e.target.value })} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="clinico-client-moment">
                  <option value="">Não especificado</option>
                  <option value="jejum">Jejum (alvo: &lt; 95 mg/dL)</option>
                  <option value="pos_prandial_1h">1h pós-prandial (alvo: &lt; 140 mg/dL)</option>
                  <option value="pos_prandial_2h">2h pós-prandial (alvo: &lt; 120 mg/dL)</option>
                  <option value="casual">Casual / outro</option>
                </select>
              </div>
            )}
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Notas (opcional)</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="rounded-xl border-brand-border text-sm" placeholder="Em jejum, pós-prandial..." data-testid="clinico-client-notes" />
            </div>
            <button onClick={save} className="w-full bg-brand-green text-white rounded-full py-2.5 text-sm font-medium" data-testid="clinico-client-save">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
